import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";

const cards = [
  { key: "appointments", label: "Scheduled", icon: EventIcon, bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300", iconBg: "bg-amber-500", ring: "ring-amber-400/60" },
  { key: "pending", label: "Upcoming", icon: ScheduleIcon, bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-700 dark:text-orange-300", iconBg: "bg-orange-500", ring: "ring-orange-400/60" },
  { key: "waiting", label: "Waiting", icon: HourglassBottomIcon, bg: "bg-violet-100 dark:bg-violet-900/40", text: "text-violet-700 dark:text-violet-300", iconBg: "bg-violet-500", ring: "ring-violet-400/60" },
  { key: "inqueue", label: "In Queue", icon: PeopleIcon, bg: "bg-sky-100 dark:bg-sky-900/40", text: "text-sky-700 dark:text-sky-300", iconBg: "bg-sky-500", ring: "ring-sky-400/60" },
  { key: "ongoing", label: "Ongoing", icon: PlayCircleIcon, bg: "bg-lime-100 dark:bg-lime-900/40", text: "text-lime-700 dark:text-lime-300", iconBg: "bg-lime-500", ring: "ring-lime-400/60" },
  { key: "completed", label: "Completed", icon: CheckCircleIcon, bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300", iconBg: "bg-emerald-500", ring: "ring-emerald-400/60" },
  { key: "cancelled", label: "Cancelled", icon: CancelIcon, bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300", iconBg: "bg-red-500", ring: "ring-red-400/60" },
  { key: "reschudle", label: "Rescheduled", icon: EventRepeatIcon, bg: "bg-cyan-100 dark:bg-cyan-900/40", text: "text-cyan-700 dark:text-cyan-300", iconBg: "bg-cyan-500", ring: "ring-cyan-400/60" },
  { key: "noshow", label: "No Show", icon: VisibilityOffIcon, bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-700 dark:text-slate-200", iconBg: "bg-slate-500", ring: "ring-slate-400/60" },
];

function AppointmentCounts({
  count,
  compact = false,
  activeKey,
  onSelect,
  selectableKeys,
}: {
  count: Record<string, number | undefined>;
  compact?: boolean;
  activeKey?: string;
  onSelect?: (key: string) => void;
  selectableKeys?: string[];
}) {
  if (compact) {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
        {cards.map(({ key, label, icon: Icon, bg, text, iconBg, ring }) => {
          const isSelectable =
            onSelect != null &&
            (selectableKeys == null || selectableKeys.includes(key));
          const isActive = activeKey === key;

          const inner = (
            <>
              <div
                className={`hidden sm:flex w-7 h-7 shrink-0 ${iconBg} rounded-md items-center justify-center shadow-sm`}
              >
                <Icon className="text-white" sx={{ fontSize: 16 }} />
              </div>
              <div className="min-w-0">
                <p className={`text-base sm:text-lg font-bold ${text} leading-none tabular-nums`}>
                  {count?.[key] ?? 0}
                </p>
                <p
                  className={`text-[9px] sm:text-[10px] font-medium ${text} uppercase tracking-wide truncate`}
                >
                  {label}
                </p>
              </div>
            </>
          );

          const className = [
            bg,
            "rounded-xl px-2.5 py-2.5 flex items-center gap-2 min-w-0 transition-all duration-200",
            isSelectable
              ? "cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
              : "",
            isActive ? `ring-2 ${ring} shadow-md scale-[1.02]` : "",
          ]
            .filter(Boolean)
            .join(" ");

          if (isSelectable) {
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                className={className}
                aria-pressed={isActive}
                title={`Show ${label.toLowerCase()} appointments`}
              >
                {inner}
              </button>
            );
          }

          return (
            <div key={key} className={className}>
              {inner}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 p-2">
      {cards.map(({ key, label, icon: Icon, bg, text, iconBg }) => (
        <div key={key} className={`${bg} rounded-lg sm:rounded-xl p-1 sm:p-2 flex flex-row sm:flex-col items-center justify-center transition-colors`}>
          <div className="hidden sm:flex w-8 h-8 items-center justify-center sm:mb-2">
            <div className={`w-8 h-8 ${iconBg} rounded-lg flex items-center justify-center`}>
              <Icon className="text-white" sx={{ fontSize: 20 }} />
            </div>
          </div>
          <div className="flex flex-row sm:flex-col items-center justify-center gap-1.5 sm:gap-0">
            <h3 className={`text-sm sm:text-xl font-bold ${text} leading-tight`}>{count?.[key] ?? 0}</h3>
            <span className={`text-[6px] sm:text-[8px] font-medium ${text} uppercase tracking-wide text-center leading-tight`}>{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default AppointmentCounts;

